import React, { createContext, useReducer, useCallback } from 'react';
import {
    HeroSliderAdminState,
    HeroSliderAdminAction,
    HeroSliderPublicState,
    HeroSliderPublicAction,
    HeroSliderData,
} from '../types/heroSlider.types';
import { heroSliderService } from '../services/heroSliderService';

interface ListApiResponse {
    msg: string;
    data: HeroSliderData[];
    total: number;
    page: number;
    totalPages: number;
    limit: number;
}
interface SingleApiResponse { msg: string; data: HeroSliderData; }

/* ─────────────────────────── Admin Context ─────────────────────────── */

const adminInitial: HeroSliderAdminState = {
    banners:    [],
    selected:   null,
    loading:    false,
    error:      null,
    pagination: { currentPage: 1, totalPages: 1, total: 0, limit: 10 },
};

function adminReducer(state: HeroSliderAdminState, action: HeroSliderAdminAction): HeroSliderAdminState {
    switch (action.type) {
        case 'FETCH_START':
            return { ...state, loading: true, error: null };
        case 'FETCH_SUCCESS':
            return {
                ...state,
                loading: false,
                banners: action.payload.data,
                pagination: {
                    currentPage: action.payload.page,
                    totalPages:  action.payload.totalPages,
                    total:       action.payload.total,
                    limit:       action.payload.limit,
                },
            };
        case 'FETCH_ERROR':
            return { ...state, loading: false, error: action.payload };
        case 'SELECT':
            return { ...state, selected: action.payload };
        case 'CREATE_SUCCESS':
            return {
                ...state,
                banners: [...state.banners, action.payload],
                pagination: { ...state.pagination, total: state.pagination.total + 1 },
            };
        case 'UPDATE_SUCCESS':
            return {
                ...state,
                banners: state.banners.map(b => b._id === action.payload._id ? action.payload : b),
                selected: null,
            };
        case 'DELETE_SUCCESS':
            return {
                ...state,
                banners: state.banners.filter(b => b._id !== action.payload),
                pagination: { ...state.pagination, total: Math.max(0, state.pagination.total - 1) },
            };
        case 'TOGGLE_STATUS_SUCCESS':
            return {
                ...state,
                banners: state.banners.map(b => b._id === action.payload._id ? action.payload : b),
            };
        case 'CLEAR_ERROR':
            return { ...state, error: null };
        default:
            return state;
    }
}

interface AdminContextValue {
    state:    HeroSliderAdminState;
    dispatch: React.Dispatch<HeroSliderAdminAction>;
    fetchAll:      (token: string, page?: number, limit?: number, status?: string, sortBy?: string, sortOrder?: 'asc' | 'desc') => Promise<void>;
    createBanner:  (token: string, formData: FormData) => Promise<HeroSliderData>;
    updateBanner:  (token: string, id: string, formData: FormData) => Promise<HeroSliderData>;
    deleteBanner:  (token: string, id: string) => Promise<void>;
    toggleStatus:  (token: string, id: string, status: string) => Promise<HeroSliderData>;
}

export const HeroSliderAdminContext = createContext<AdminContextValue | null>(null);

export const HeroSliderAdminProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [state, dispatch] = useReducer(adminReducer, adminInitial);

    const fetchAll = useCallback(async (token: string, page = 1, limit = 10, status?: string, sortBy = 'bannerOrder', sortOrder: 'asc' | 'desc' = 'asc') => {
        dispatch({ type: 'FETCH_START' });
        try {
            const { data } = await heroSliderService.fetchAll(token, page, limit, status, sortBy, sortOrder);
            dispatch({ type: 'FETCH_SUCCESS', payload: data as ListApiResponse });
        } catch (err: any) {
            dispatch({ type: 'FETCH_ERROR', payload: err?.response?.data?.errors?.[0]?.msg ?? 'Failed to load banners' });
        }
    }, []);

    const createBanner = useCallback(async (token: string, formData: FormData): Promise<HeroSliderData> => {
        const { data } = await heroSliderService.create(token, formData);
        const res = data as SingleApiResponse;
        dispatch({ type: 'CREATE_SUCCESS', payload: res.data });
        return res.data;
    }, []);

    const updateBanner = useCallback(async (token: string, id: string, formData: FormData): Promise<HeroSliderData> => {
        const { data } = await heroSliderService.update(token, id, formData);
        const res = data as SingleApiResponse;
        dispatch({ type: 'UPDATE_SUCCESS', payload: res.data });
        return res.data;
    }, []);

    const deleteBanner = useCallback(async (token: string, id: string): Promise<void> => {
        await heroSliderService.delete(token, id);
        dispatch({ type: 'DELETE_SUCCESS', payload: id });
    }, []);

    const toggleStatus = useCallback(async (token: string, id: string, status: string): Promise<HeroSliderData> => {
        const { data } = await heroSliderService.toggleStatus(token, id, status);
        const res = data as SingleApiResponse;
        dispatch({ type: 'TOGGLE_STATUS_SUCCESS', payload: res.data });
        return res.data;
    }, []);

    return (
        <HeroSliderAdminContext.Provider value={{ state, dispatch, fetchAll, createBanner, updateBanner, deleteBanner, toggleStatus }}>
            {children}
        </HeroSliderAdminContext.Provider>
    );
};

/* ─────────────────────────── Public Context ─────────────────────────── */

const publicInitial: HeroSliderPublicState = { banners: [], loading: false, error: null };

function publicReducer(state: HeroSliderPublicState, action: HeroSliderPublicAction): HeroSliderPublicState {
    switch (action.type) {
        case 'FETCH_START':
            return { ...state, loading: true, error: null };
        case 'FETCH_SUCCESS':
            return { ...state, loading: false, banners: action.payload };
        case 'FETCH_ERROR':
            return { ...state, loading: false, error: action.payload };
        default:
            return state;
    }
}

interface PublicContextValue {
    state:      HeroSliderPublicState;
    fetchPublic: (audience: string) => Promise<void>;
}

export const HeroSliderPublicContext = createContext<PublicContextValue | null>(null);

export const HeroSliderPublicProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [state, dispatch] = useReducer(publicReducer, publicInitial);

    const fetchPublic = useCallback(async (audience: string) => {
        dispatch({ type: 'FETCH_START' });
        try {
            const { data } = await heroSliderService.fetchPublic(audience);
            const res = data as { data: HeroSliderData[] };
            dispatch({ type: 'FETCH_SUCCESS', payload: res.data });
        } catch {
            dispatch({ type: 'FETCH_ERROR', payload: 'Failed to load banners' });
        }
    }, []);

    return (
        <HeroSliderPublicContext.Provider value={{ state, fetchPublic }}>
            {children}
        </HeroSliderPublicContext.Provider>
    );
};
